import type { ReactNode } from "react";
import { StyleSheet } from "react-native-unistyles";
import { Autocomplete } from "@visioni/a3s-ui/components";
import { View } from "@src/components";
import type { DefaultTimeZone, RawActivationFilters } from "@visioni/a3s-monitoring/app";
import type { CameraOption, SiteOption } from "../hooks/useActivationScopeOptions";
import { OrderField } from "./OrderField";
import { TimeBoundFields } from "./TimeBoundFields";
import { TimeZoneField } from "./TimeZoneField";

/** The filters as the URL carries them: ids, wall clocks in `tz`, `tz` itself, and the order. */
export type ActivationFilterValues = {
  readonly [K in keyof RawActivationFilters]?: string;
};

interface ActivationFilterBarProps {
  readonly values: ActivationFilterValues;
  readonly onChange: (next: ActivationFilterValues) => void;
  readonly sites: readonly SiteOption[];
  readonly cameras: readonly CameraOption[];
  readonly isLoading: boolean;
  /** The zone applied. */
  readonly timeZone: string;
  readonly fallback: DefaultTimeZone | undefined;
  /** When the timezone offsets are shown for. */
  readonly offsetsAt: Date;
  /** What an empty From means for this list. Defaults to its whole history. */
  readonly fromPlaceholder?: string;
  /** Whether Default means the site's zone when a site is selected, as it does unless the list says otherwise. */
  readonly siteTimeZoneDefault?: boolean;
  /** Another field for the last row, beside the order. */
  readonly trailing?: ReactNode;
}

const siteId = (s: SiteOption) => s.id;
const siteLabel = (s: SiteOption) => s.label;
const cameraKey = (c: CameraOption) => c.key;
const cameraLabel = (c: CameraOption) => c.label;

/**
 * Site, then optionally a camera at that site; then the timezone with the
 * optional start and end it reads them in; then the order.
 *
 * The camera picker offers only the selected site's cameras, so it stays
 * disabled until a site is chosen, and changing the site clears the camera:
 * the replica indexes a camera under its site, so a camera from another site
 * would filter to nothing.
 *
 * A filter the URL applies for a site or camera this tenant no longer lists
 * shows its raw id, so a shared or detail-page link never looks unfiltered.
 */
export function ActivationFilterBar({
  values,
  onChange,
  sites,
  cameras,
  isLoading,
  timeZone,
  fallback,
  offsetsAt,
  fromPlaceholder = "From the beginning",
  siteTimeZoneDefault = true,
  trailing,
}: ActivationFilterBarProps) {
  const selectedSite: SiteOption | null =
    values.site === undefined ?
      null
    : (sites.find((s) => s.id === values.site) ?? { id: values.site, label: values.site });
  const siteCameras = values.site === undefined ? [] : cameras.filter((c) => c.siteId === values.site);
  const selectedCamera: CameraOption | null =
    values.camera === undefined ?
      null
    : (siteCameras.find((c) => c.key === values.camera) ?? {
        key: values.camera,
        siteId: values.site ?? "",
        label: values.camera,
      });

  const update = (patch: ActivationFilterValues) => onChange({ ...values, ...patch });

  return (
    <View style={styles.rows}>
      <View style={[styles.row, styles.top]}>
        <View style={[styles.field, styles.top]}>
          <Autocomplete<SiteOption>
            options={sites}
            value={selectedSite}
            onChange={(site) => update({ site: site?.id, camera: undefined })}
            getOptionLabel={siteLabel}
            getOptionId={siteId}
            isLoading={isLoading}
            placeholder="All sites"
            label="Site"
            floatLabel="always"
          />
        </View>
        <View style={[styles.field, styles.middle]}>
          <Autocomplete<CameraOption>
            options={siteCameras}
            value={selectedCamera}
            onChange={(camera) => update({ camera: camera?.key })}
            getOptionLabel={cameraLabel}
            getOptionId={cameraKey}
            isLoading={isLoading}
            disabled={values.site === undefined}
            placeholder={values.site === undefined ? "Choose a site first" : "All cameras"}
            label="Camera"
            floatLabel="always"
          />
        </View>
      </View>
      <View style={styles.middle}>
        <TimeBoundFields
          value={values}
          onChange={update}
          fromPlaceholder={fromPlaceholder}
          // Beside the bounds it is read in.
          leading={
            <TimeZoneField
              hasSiteFilter={siteTimeZoneDefault}
              value={timeZone}
              fallback={fallback}
              at={offsetsAt}
              onChange={(tz) => update({ tz })}
            />
          }
        />
      </View>
      <View style={styles.row}>
        <View style={styles.order}>
          <OrderField value={values.order} onChange={(order) => update({ order })} />
        </View>
        {trailing !== undefined && <View style={styles.order}>{trailing}</View>}
      </View>
    </View>
  );
}

const styles = StyleSheet.create((theme) => ({
  rows: { gap: theme.spacing.lg },
  // Same proportions as the clip compilation filters.
  row: { flexDirection: "row", flexWrap: "wrap", alignItems: "flex-start", gap: theme.spacing.lg },
  field: { flexBasis: "22%", flexGrow: 1, minWidth: 160 },
  // The width of the timezone field above it.
  order: { flexBasis: "24%", minWidth: 220 },
  // Each dropdown opens over the fields after it, so earlier fields stack higher.
  top: { zIndex: 3 },
  middle: { zIndex: 2 },
  bottom: { zIndex: 1 },
}));
